import type { RovoNavigationConversation } from '@atlassian/rovo-spaces/types/rovo-navigation-conversation';

export type LocalSpaceConversation = Omit<RovoNavigationConversation, 'isEmpty'> & {
	isEmpty?: boolean;
};

const toSpaceConversation = (
	conversation: LocalSpaceConversation,
	spaceId: string,
): RovoNavigationConversation => ({
	...conversation,
	rovoSpaceId: conversation.rovoSpaceId ?? spaceId,
	isEmpty: conversation.isEmpty ?? !conversation.lastMessage?.trim(),
});

const getTimestamp = (conversation: RovoNavigationConversation): number => {
	const timestamp = conversation.lastMessageTimestamp
		? new Date(conversation.lastMessageTimestamp).getTime()
		: 0;

	return Number.isNaN(timestamp) ? 0 : timestamp;
};

export const mergeSpaceConversations = ({
	apiConversations,
	localConversations,
	spaceId,
}: {
	apiConversations: RovoNavigationConversation[];
	localConversations: LocalSpaceConversation[];
	spaceId: string;
}): RovoNavigationConversation[] => {
	const localSpaceConversations = localConversations
		.filter((conversation) => conversation.rovoSpaceId === spaceId)
		.map((conversation) => toSpaceConversation(conversation, spaceId));
	const localById = new Map(
		localSpaceConversations.map((conversation) => [conversation.id, conversation]),
	);
	const localByBackendId = new Map(
		localSpaceConversations
			.filter((conversation) => Boolean(conversation.backendConversationId))
			.map((conversation) => [conversation.backendConversationId as string, conversation]),
	);
	const consumedLocalIds = new Set<string>();

	const mergedFromApi = apiConversations.map((apiConversation) => {
		const local = localById.get(apiConversation.id) ?? localByBackendId.get(apiConversation.id);
		if (!local) {
			return apiConversation;
		}
		consumedLocalIds.add(local.id);
		const localLastMessage = local.lastMessage?.trim() ? local.lastMessage : undefined;
		return {
			...apiConversation,
			name: local.name ?? apiConversation.name,
			lastMessage: localLastMessage ?? apiConversation.lastMessage,
			lastMessageType: local.lastMessageType ?? apiConversation.lastMessageType,
			lastMessageTimestamp: localLastMessage
				? (local.lastMessageTimestamp ?? apiConversation.lastMessageTimestamp)
				: apiConversation.lastMessageTimestamp,
			lastAgentMessageStatus:
				local.lastAgentMessageStatus ?? apiConversation.lastAgentMessageStatus,
			agent: local.agent ?? apiConversation.agent,
			readStatus: local.readStatus ?? apiConversation.readStatus,
			scopes: local.scopes ?? apiConversation.scopes,
			tags: local.tags ?? apiConversation.tags,
			currentTask: local.currentTask ?? apiConversation.currentTask,
		};
	});

	const localOnly = localSpaceConversations.filter(
		(conversation) => !consumedLocalIds.has(conversation.id),
	);
	return [...localOnly, ...mergedFromApi].sort((a, b) => getTimestamp(b) - getTimestamp(a));
};
